import React, {useState} from "react";
import "react-datepicker/dist/react-datepicker.css";
import {MdStar, MdStarBorder} from "react-icons/md";
import {RecentReviewsWrapper} from "components/atoms/Store/Overview/RecentReview/RecentReview.styles";
import Button from "components/molecules/Button";

const DEFAULT_AVATAR = "https://ui-avatars.com/api/?background=F15412&color=fff&name=";

const formatDate = dateStr => {
    if (!dateStr) return "";
    try {
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return "";
    }
};

const StarRow = ({rating = 0}) => (
    <div className="stars">
        {Array.from({length: 5}, (_, i) =>
            i < Math.round(rating) ? <MdStar key={i} /> : <MdStarBorder key={i} style={{color: "#ddd"}} />
        )}
    </div>
);

const getInitials = name => {
    if (!name) return "U";
    return name
        .split(" ")
        .map(n => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
};

const RecentReviews = ({data = [], onReply}) => {
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleReplySubmit = async reviewId => {
        if (!replyText.trim()) return;
        setSubmitting(true);
        try {
            await onReply(reviewId, replyText.trim());
            setReplyingTo(null);
            setReplyText("");
        } catch (err) {
            console.error("Reply error:", err);
        } finally {
            setSubmitting(false);
        }
    };

    if (!data || data.length === 0) {
        return (
            <RecentReviewsWrapper>
                <p style={{color: "#aaa", textAlign: "center", padding: "40px 0"}}>
                    No reviews yet. Customer reviews will appear here.
                </p>
            </RecentReviewsWrapper>
        );
    }

    return (
        <RecentReviewsWrapper>
            {data.map((review, index) => {
                const user = review.user || {};
                const profile = user.profile || {};
                const userName = profile.first_name
                    ? `${profile.first_name}${profile.last_name ? ` ${profile.last_name}` : ""}`
                    : user.email || "Customer";
                const initials = getInitials(userName);

                const avatar =
                    user?.profile_picture || user?.photo || `${DEFAULT_AVATAR}${encodeURIComponent(initials)}`;
                const hasReplied = !!review?.reply?.text;
                const isReplying = replyingTo === review._id;
                const foodRating = review.foodRating || review.rating || 0;
                const reviewText = review.foodComment || review.comment || "";

                return (
                    <div className="review-wrap" key={review._id || index}>
                        <div className="user-info">
                            <div className="user-title">
                                <img
                                    src={avatar}
                                    alt={userName}
                                    onError={e => {
                                        e.target.src = `${DEFAULT_AVATAR}${encodeURIComponent(initials)}`;
                                    }}
                                />
                                <strong>{userName}</strong>
                            </div>
                            <p>{review.created_at ? formatDate(review.created_at) : review.date || ""}</p>
                        </div>

                        <p>{reviewText}</p>

                        {/* Food tags */}
                        {review.foodTags?.length > 0 && (
                            <div style={{display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8}}>
                                {review.foodTags.map(tag => (
                                    <span
                                        key={tag}
                                        style={{
                                            padding: "2px 10px",
                                            borderRadius: 20,
                                            background: "#fff5f0",
                                            border: "1px solid #ed9321",
                                            color: "#ed9321",
                                            fontSize: 12,
                                        }}>
                                        {tag.replace(/_/g, " ")}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="rating-wrap">
                            <StarRow rating={foodRating} />
                            {hasReplied ? (
                                <Button
                                    width="100px"
                                    style={{background: "#aaa", cursor: "default"}}
                                    onClick={() => setReplyingTo(isReplying ? null : review._id)}>
                                    Replied
                                </Button>
                            ) : (
                                <Button
                                    width="100px"
                                    onClick={() => {
                                        setReplyingTo(isReplying ? null : review._id);
                                        setReplyText("");
                                    }}>
                                    Reply
                                </Button>
                            )}
                        </div>

                        {/* Existing reply shown */}
                        {hasReplied && (
                            <div
                                style={{
                                    marginTop: 8,
                                    padding: "10px 14px",
                                    background: "#f9f9f9",
                                    borderLeft: "3px solid #ed9321",
                                    borderRadius: "0 6px 6px 0",
                                    fontSize: 13,
                                    color: "#555",
                                }}>
                                <strong style={{color: "#ed9321", display: "block", marginBottom: 2}}>
                                    Your Reply
                                </strong>
                                {review.reply.text}
                                <span style={{color: "#bbb", fontSize: 11, marginLeft: 8}}>
                                    · {formatDate(review.reply.repliedAt)}
                                </span>
                            </div>
                        )}

                        {/* Reply input box */}
                        {isReplying && !hasReplied && (
                            <div style={{marginTop: 10}}>
                                <textarea
                                    value={replyText}
                                    onChange={e => setReplyText(e.target.value)}
                                    placeholder="Write your reply..."
                                    rows={3}
                                    style={{
                                        width: "100%",
                                        padding: "10px 14px",
                                        border: "1px solid #ddd",
                                        borderRadius: 8,
                                        fontSize: 14,
                                        fontFamily: "inherit",
                                        resize: "none",
                                        outline: "none",
                                        boxSizing: "border-box",
                                    }}
                                    onFocus={e => (e.target.style.borderColor = "#ed9321")}
                                    onBlur={e => (e.target.style.borderColor = "#ddd")}
                                />
                                <div style={{display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end"}}>
                                    <button
                                        onClick={() => {
                                            setReplyingTo(null);
                                            setReplyText("");
                                        }}
                                        style={{
                                            padding: "6px 16px",
                                            border: "1px solid #ddd",
                                            borderRadius: 6,
                                            background: "white",
                                            cursor: "pointer",
                                            fontSize: 13,
                                        }}>
                                        Cancel
                                    </button>
                                    <Button
                                        width="auto"
                                        disabled={submitting || !replyText.trim()}
                                        onClick={() => handleReplySubmit(review._id)}
                                        style={{padding: "6px 16px", fontSize: 13}}>
                                        {submitting ? "Sending..." : "Send Reply"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </RecentReviewsWrapper>
    );
};

export default RecentReviews;
